// API/axios.ts
import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";

// --- Configuration ---
const API_URL = import.meta.env.VITE_BASE_URL;

// --- Types ---
interface FailedRequest {
  resolve: (token: string) => void;
  reject: (error: any) => void;
}

// --- State Variables ---
let isRefreshing = false;
let failedQueue: FailedRequest[] = [];

// --- Helper: Queue Process ---
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

// --- Axios Instance ---
const API = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// --- Request Interceptor ---
API.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("sellerAccessToken");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// --- Response Interceptor ---
API.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url?.includes("/refresh-token")) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return API(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("sellerRefreshToken");

      if (!refreshToken) {
        return Promise.reject(error);
      }

      try {
        const response = await API.post(`/auth/refresh-token`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;

        if (accessToken) {
          localStorage.setItem("sellerAccessToken", accessToken);
          if (newRefreshToken) {
            localStorage.setItem("sellerRefreshToken", newRefreshToken);
          }

          API.defaults.headers.common["Authorization"] =
            `Bearer ${accessToken}`;
          processQueue(null, accessToken);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }
          return API(originalRequest);
        } else {
          throw new Error("No access token received");
        }
      } catch (refreshError) {
        console.error("Refresh token failed:", refreshError);
        processQueue(refreshError, null);
        // Redirect to login on refresh failure
        window.location.href = "/seller/login";
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// --- Category API Methods ---
export const fetchCategories = async () => {
  const response = await API.get("/admin/categories");
  return response.data.categories || response.data;
};

// --- Product API Methods ---
export const fetchSellerProducts = async () => {
  const response = await API.get("/seller/my-products");
  return response.data.products;
};

export const fetchProductById = async (productId: string) => {
  const response = await API.get(`/seller/product/${productId}`);
  return response.data.product;
};

export const createProduct = async (productData: any) => {
  try {
    const formData = new FormData();

    // Debug: Log incoming data
    console.log("createProduct received:", productData);

    // Check each field and append with proper validation
    if (!productData.name) {
      throw new Error("Product name is required");
    }
    formData.append("name", productData.name);

    if (!productData.description) {
      throw new Error("Product description is required");
    }
    formData.append("description", productData.description);

    if (!productData.categoryId) {
      throw new Error("Category is required");
    }
    formData.append("categoryId", productData.categoryId);

    // Handle variants
    if (!productData.variants || productData.variants?.length === 0) {
      throw new Error("At least one variant is required");
    }

    // Process variants to ensure numbers are numbers
    const processedVariants = productData.variants.map((v: any) => ({
      title: String(v.title || ""),
      unit: String(v.unit || ""),
      value: Number(v.value) || 0,
      price: Number(v.price) || 0,
      sku: String(v.sku || ""),
      stock: Number(v.stock) || 0,
    }));

    formData.append("variants", JSON.stringify(processedVariants));

    // Handle images
    if (!productData.images || productData.images?.length === 0) {
      throw new Error("At least one image is required");
    }

    // Append each image
    productData.images.forEach((image: File, index: number) => {
      if (image instanceof File) {
        formData.append("images", image);
        console.log(`Appended image ${index + 1}:`, image.name);
      } else {
        console.warn(`Image at index ${index} is not a File object:`, image);
      }
    });

    // Debug: Log FormData contents
    console.log("FormData entries:");
    for (let pair of formData.entries()) {
      if (pair[0] === "images") {
        console.log(pair[0] + ": [File] " + (pair[1] as File).name);
      } else if (pair[0] === "variants") {
        console.log(pair[0] + ": " + pair[1]);
      } else {
        console.log(pair[0] + ": " + pair[1]);
      }
    }

    const response = await API.post("/seller/create-product", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.product;
  } catch (error) {
    console.error("Error in createProduct:", error);
    throw error;
  }
};

export const updateProduct = async (productId: string, formData: FormData) => {
  const response = await API.put(
    `/seller/update-product/${productId}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data.updatedProduct;
};

export const deleteProduct = async (productId: string) => {
  const response = await API.delete(`/seller/delete-product/${productId}`);
  return response.data;
};

export const updateInventory = async (
  productId: string,
  data: { variantId: string; stock: number },
) => {
  const response = await API.patch(
    `/seller/update-inventory/${productId}`,
    data,
  );
  return response.data;
};

// --- Order API Methods ---
export const fetchSellerOrders = async (
  params: {
    page?: number;
    limit?: number;
    status?: string;
    date?: string;
  } = {},
) => {
  const response = await API.get("/seller/orders", { params });
  return response.data;
};

export const fetchOrderById = async (orderId: string) => {
  const response = await API.get(`/seller/order/${orderId}`);
  return response.data;
};

export const updateOrderStatus = async (orderId: string, status: string) => {
  const response = await API.put(`/seller/order/${orderId}/status`, { status });
  return response.data;
};

export const fetchTodayOrderCount = async () => {
  const response = await API.get("/seller/orders/today-count");
  return response.data;
};

export const fetchDateWiseOrderCount = async () => {
  const response = await API.get("/seller/orders/date-wise");
  return response.data;
};

// --- Existing Seller API Methods ---
export const sellerAPI = {
  register: (data: any) => API.post("/seller/register", data),
  resendOTP: (data: { email: string }) => API.post("/seller/resend-otp", data),
  verifyEmail: (data: any) => API.post("/seller/verify-email", data),
  login: (data: any) => API.post("/seller/login", data),
  verifyGSTIN: (data: any) => API.post("/seller/verify-gstin", data),
  addProduct: (data: any) => API.post("/seller/add-product", data),
  getProducts: () => API.get("/seller/products"),
  deleteProduct: (id: string | number) => API.delete(`/seller/products/${id}`),
  getProfile: () => API.get("/seller/get-profile"),
  updateProfile: (data: {
    firstName: string;
    lastName: string;
    phone: string;
    businessAddress: string;
  }) => API.put("/seller/update-profile", data),
  requestBankOTP: () => API.post("/seller/request-bank-update-otp"),
  updateBankDetails: (data: {
    bankAccount: string;
    ifscCode: string;
    otp: string;
  }) => API.put("/seller/update-bank-details", data),
  submitKYC: (data: { panNumber: string; businessAddress: string }) =>
    API.post("/seller/submit-kyc", data),
  uploadPanDocument: (file: File) => {
    const formData = new FormData();
    formData.append("type", "pan");
    formData.append("file", file);
    return API.post("/seller/upload-document", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  uploadSignature: (data: FormData) =>
    API.post("/seller/upload-signature", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  uploadAddressProof: (data: FormData) =>
    API.post("/seller/upload-address-proof", data, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
};

export default API;
